"use client";

import emailjs from "@emailjs/browser";
import { zodResolver } from "@hookform/resolvers/zod";
import dynamic from "next/dynamic";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { Icons } from "./icons";

import { env } from "@/env.mjs";

const formSchema = z.object({
  user_name: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
  user_phone_number: z.string().min(2, {
    message: "Phone number must be at least 2 characters.",
  }),
  user_service: z.string().min(2, {
    message: "Services can't be empty.",
  }),
  user_email: z.string().email(),
  message: z.string().min(2, {
    message: "Message can't be empty",
  }),
});

export function Contact() {
  const Map = useMemo(
    () => dynamic(() => import("@/components/map"), { ssr: false }),
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      user_name: "",
      user_phone_number: "",
      user_service: "",
      user_email: "",
      message: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);

      await emailjs.sendForm(
        env.NEXT_PUBLIC_EMAILJS_SERVICE_ID,
        env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID,
        formRef.current as HTMLFormElement,
        env.NEXT_PUBLIC_EMAILJS_API_KEY
      );

      formRef.current?.reset();
      form.reset();
      toast({ description: "Message sent" });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.log("SEND_MESSAGE_ERROR: ", error);
      }
      toast({
        description: "Something went wrong. Please try again!",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="px-6 py-16">
      <div className="max-w-7xl mb-6 md:mb-0 mx-auto flex flex-col gap-6 items-center w-full">
        <div className="text-center flex flex-col gap-4 mb-7">
          <h1 className="bg-clip-text text-transparent font-bold text-[max(38px,min(4vw,50px))] leading-tight tracking-tighter relative bg-[linear-gradient(180deg,#555,#000)] bg-black bg-[center_center] animate-move">
            Contact
          </h1>
          <p className="text-base font-medium text-gray-600 max-w-[23.375rem]">
            Get in touch with us. {"We're"} ready to help you find the best
            solutions.
          </p>
        </div>
        <div className="w-full flex flex-col md:min-h-[30rem] md:flex-row gap-6 md:gap-16 justify-between items-center">
          <div className="md:w-1/2 w-full">
            <Map />
          </div>
          <div className="md:w-1/2 w-full">
            <div className="flex items-center justify-between gap-6">
              <Form {...form}>
                <form
                  ref={formRef}
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="w-full flex flex-col gap-y-6"
                >
                  <FormField
                    control={form.control}
                    name="user_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="uppercase tracking-wide text-gray-600 text-xs font-bold mb-2">
                          Your name
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Jane Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="user_phone_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="uppercase tracking-wide text-gray-600 text-xs font-bold mb-2">
                          Phone number
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="+233 50 234 2456" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="user_service"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="uppercase tracking-wide text-gray-600 text-xs font-bold mb-2">
                          Choose your service
                        </FormLabel>
                        <Select
                          name="user_service"
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>Services</SelectLabel>

                              <SelectItem value="Building Construction">
                                Building Construction
                              </SelectItem>
                              <SelectItem value="Sales of Buildings">
                                Sales of Buildings
                              </SelectItem>
                              <SelectItem value="House Rentals">
                                House Rentals
                              </SelectItem>
                              <SelectItem value="Car Sales">
                                Car Sales
                              </SelectItem>
                            </SelectGroup>

                            <SelectSeparator />

                            <SelectGroup>
                              <SelectLabel>Products</SelectLabel>
                              <SelectItem value="Poultry">Poultry</SelectItem>
                              <SelectItem value="Pig farm">Pig farm</SelectItem>
                              <SelectItem value="Fish farm">
                                Fish farm
                              </SelectItem>
                            </SelectGroup>

                            <SelectSeparator />

                            <SelectGroup>
                              <SelectLabel>Automobiles</SelectLabel>
                              <SelectItem value="Taxis">Taxis</SelectItem>
                              <SelectItem value="Trucks">Trucks</SelectItem>
                              <SelectItem value="Buses">Buses</SelectItem>
                            </SelectGroup>

                            <SelectSeparator />

                            <SelectGroup>
                              <SelectLabel>Shipping</SelectLabel>
                              <SelectItem value="Air freight">
                                Air freight
                              </SelectItem>
                              <SelectItem value="Sea freight">
                                Sea freight
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="user_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="uppercase tracking-wide text-gray-600 text-xs font-bold mb-2">
                          Email address
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="jane.doe@example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="uppercase tracking-wide text-gray-600 text-xs font-bold mb-2">
                          Message
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter your message"
                            className="resize-none"
                            rows={6}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="lg:w-1/3 w-max"
                    disabled={isLoading}
                    aria-disabled={isLoading}
                  >
                    {isLoading && (
                      <Icons.spinner
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {isLoading ? "Sending..." : "Send message"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>

        <div className="w-full bg-gray-100 text-gray-900 py-12 px-4 mt-8">
          <div className="max-w-3xl mx-auto w-full flex gap-4 items-start justify-between [@media(max-width:740px)]:flex-wrap">
            <div className="flex flex-col items-start">
              <h2 className="text-base font-bold mb-2">Call us</h2>
              <p className="text-sm font-medium text-gray-500">
                +1 (973) 393-6675
              </p>
              <p className="text-sm font-medium text-gray-500">
                +233 (30) 250-6245
              </p>
            </div>

            <div className="flex flex-col items-start">
              <h2 className="text-sm font-bold mb-2">Email us</h2>
              <p className="text-sm font-medium text-gray-500">
                info@sloyag.com
              </p>
            </div>

            <div className="flex flex-col items-start">
              <h2 className="text-base font-bold mb-2">Visit us</h2>
              <p className="text-sm font-medium text-gray-500">
                <span className="block">Norkor Tso Crescent</span>
                <span className="block">GD-246-7805, East Legon</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
